import { Box, Typography, useMediaQuery } from '@mui/material';
import React from 'react';

import { NAV_BAR_MOBILE_BREAKPOINT, NAV_COLOR_PALETTE } from '../Components/styleConstants';
import { useMenuContext } from '../ContextState/useMenuContext';
import { DATA_TEST, getDynamicDataTestId } from '../enums';
import { IMenuItem } from '../types';
import { handleTabOrEscapeToLeaveSubmenu } from '../utils/keyboardNavUtils';

import { DragAndDropList } from './DragAndDropList/DragAndDropList';
import { DragAndDropListItem } from './DragAndDropList/DragAndDropListItem';
import { FavoritesSectionItem } from './FavoritesSectionItem';
import { handleFocusBetweenFavoriteItems, moveFocusUponDeletion } from './utils';

export interface IFavoritesSectionProps {
  sectionId: string;
  sectionLabel: string;
  sectionItems: IMenuItem[];
  sectionIndex: number;
  fullPageWidth?: boolean;
}

export const FavoritesSection: React.FC<IFavoritesSectionProps> = ({
  sectionItems,
  sectionId,
  sectionLabel,
  sectionIndex,
  fullPageWidth
}: IFavoritesSectionProps) => {
  const {
    onFavoriteItemDragEnd,
    onFavoriteDeleteClick,
    setShowFavoritesSubmenu,
    currentLevelOneItemRef,
    favoritesRefs
  } = useMenuContext();
  const deleteButtonRefs = React.useRef<{ [key: string]: React.RefObject<HTMLButtonElement> }>({});
  const textRefs = React.useRef<{ [key: string]: React.RefObject<HTMLDivElement> }>({});
  const mobileWidth = useMediaQuery(`(max-width:${NAV_BAR_MOBILE_BREAKPOINT}px)`);
  const [refsSetUp, setRefsSetUp] = React.useState(false);

  React.useEffect(() => {
    sectionItems.forEach((item) => {
      deleteButtonRefs.current[item.id] = deleteButtonRefs.current[item.id] || React.createRef<HTMLButtonElement>();
      textRefs.current[item.id] = textRefs.current[item.id] || React.createRef<HTMLDivElement>();
    });
    setRefsSetUp(true);
  }, [sectionItems]);

  const handleKeyDown = (event, itemIndex, textRef, deleteButtonRef) => {
    // If the focus is on the delete button or the text, don't focus on the text again
    const focusIsOnOuterElement = ![textRef?.current, deleteButtonRef?.current].includes(document.activeElement);
    if (event.key === 'Enter' && focusIsOnOuterElement && refsSetUp && textRef?.current) {
      event.preventDefault();
      textRef.current.focus();
    }
    handleFocusBetweenFavoriteItems(event.key, sectionIndex, itemIndex, favoritesRefs);
    if (currentLevelOneItemRef) {
      handleTabOrEscapeToLeaveSubmenu(event.key, currentLevelOneItemRef?.current);
    }
  };
  return (
    <Box
      data-test={getDynamicDataTestId(DATA_TEST.favorites_section_container, sectionId)}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        marginTop: 1.5
      }}
    >
      <Typography
        data-test={getDynamicDataTestId(DATA_TEST.favorites_section, sectionLabel)}
        variant={mobileWidth ? 'h4' : 'h5'}
        component="label"
        sx={{ color: NAV_COLOR_PALETTE.primaryGray, mb: 1.5 }}
      >
        {sectionLabel}
      </Typography>

      <DragAndDropList
        prefix={`favorites_${sectionId}`}
        dataTest={getDynamicDataTestId(DATA_TEST.draggable_favorites, sectionId)}
        onDragEnd={async (sourceIndex, destinationIndex) => {
          await onFavoriteItemDragEnd(sourceIndex, destinationIndex, { items: sectionItems, id: sectionId });
        }}
      >
        {sectionItems.map((favoriteItem: IMenuItem, idx: number) => {
          const textRef = textRefs.current[favoriteItem.id];
          const deleteButtonRef = deleteButtonRefs.current[favoriteItem.id];
          const key = fullPageWidth ? `full_page_${favoriteItem.id}` : `nav_bar_${favoriteItem.id}`;

          return (
            <DragAndDropListItem
              id={favoriteItem.id}
              index={idx}
              key={key}
              dataTest={`favorites_item_${favoriteItem.id}`}
              itemRef={favoritesRefs?.[sectionIndex]?.[idx] || null}
              handleKeyDown={(event) => handleKeyDown(event, idx, textRef, deleteButtonRef)}
            >
              <FavoritesSectionItem
                onClick={() => {
                  favoriteItem.onClick?.();
                  setShowFavoritesSubmenu(false);
                }}
                url={favoriteItem.url}
                linkComponent={favoriteItem.linkComponent}
                deleteButtonRef={deleteButtonRef}
                closeFavoritesSubmenu={() => setShowFavoritesSubmenu(false)}
                textRef={textRef}
                label={favoriteItem.label}
                handleDelete={async () => {
                  moveFocusUponDeletion(sectionIndex, idx, favoritesRefs, currentLevelOneItemRef?.current);
                  await onFavoriteDeleteClick(favoriteItem);
                }}
              />
            </DragAndDropListItem>
          );
        })}
      </DragAndDropList>
    </Box>
  );
};
